"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { InterpolationResults } from "@/components/tools/calculus/interpolation-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  parseXYPairs,
  type XYPoint,
} from "@/lib/tool-kit/calculus/data-points";
import { distinctSorted } from "@/lib/tool-kit/calculus/interpolation";

/** English strings. A translated content file overrides these through `ui`. */
export const LINEAR_INTERPOLATION_CALCULATOR_STRINGS = {
  modeLabel: "Interpolate",
  modes: {
    two: "Between two points",
    table: "Within a table of values",
    bilinear: "On a grid (bilinear)",
  },
  x1Label: "x₁",
  y1Label: "y₁",
  x2Label: "x₂",
  y2Label: "y₂",
  targetXLabel: "Find y at x =",
  targetYLabel: "Find x at y = (optional)",
  tableLabel: "Table of values, one “x, y” pair per line",
  tableHint:
    "The x-values may be in any order. A repeated x keeps its first row.",
  tablePlaceholder: "0, 1\n20, 0.8\n40, 0.6",
  gridX1: "x₁",
  gridX2: "x₂",
  gridY1: "y₁",
  gridY2: "y₂",
  q11: "f(x₁, y₁)",
  q21: "f(x₂, y₁)",
  q12: "f(x₁, y₂)",
  q22: "f(x₂, y₂)",
  gridTargetX: "Find f at x =",
  gridTargetY: "and y =",
  results: "Results",
  yResult: "y at x = {x}",
  xResult: "x at y = {y}",
  slopeResult: "Slope",
  positionResult: "Position between the points",
  valueResult: "f(x, y)",
  positionX: "Position across x",
  positionY: "Position across y",
  segmentResult: "Segment used",
  segmentText: "({x1}, {y1}) to ({x2}, {y2})",
  exactMatch: "x is in your table, so the table value is returned as it is.",
  extrapolatedBelow:
    "x is below the smallest x in the data, so this is an extrapolation along the first segment: less reliable than interpolation, because nothing constrains the curve out there.",
  extrapolatedAbove:
    "x is above the largest x in the data, so this is an extrapolation along the last segment: less reliable than interpolation, because nothing constrains the curve out there.",
  extrapolatedPoints:
    "x lies outside [x₁, x₂], so this is an extrapolation along the line through the two points.",
  extrapolatedGrid:
    "The point lies outside the grid, so this is an extrapolation.",
  workingHeading: "The working",
  formulaY: "y = y₁ + (x − x₁)(y₂ − y₁)/(x₂ − x₁)",
  workingY: "y = {y1} + ({x} − {x1})({y2} − {y1}) / ({x2} − {x1}) = {result}",
  formulaX: "x = x₁ + (y − y₁)(x₂ − x₁)/(y₂ − y₁)",
  workingX: "x = {x1} + ({y} − {y1})({x2} − {x1}) / ({y2} − {y1}) = {result}",
  inverseSolutions: "x where the data reaches y = {y}",
  inverseNone: "The data never reaches y = {y}, so no x was found.",
  inverseFlat:
    "y₁ = y₂, so the line is horizontal and x cannot be found from y.",
  gridHeading: "Bilinear working",
  gridStep1: "Along x at y = {y1}: R₁ = {q11} + {tx}·({q21} − {q11}) = {r1}",
  gridStep2: "Along x at y = {y2}: R₂ = {q12} + {tx}·({q22} − {q12}) = {r2}",
  gridStep3: "Along y: f = R₁ + {ty}·(R₂ − R₁) = {result}",
  graphHeading: "Graph",
  graphTitleTwo:
    "The line through two points with the interpolated point marked",
  graphTitleTable:
    "The data joined by straight segments with the interpolated point marked",
  seriesLine: "Line through the points",
  seriesData: "Data",
  pointLabelText: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  numberInvalid: "Enter {field} as a number.",
  sameX: "x₁ and x₂ must be different.",
  sameY: "y₁ and y₂ must be different.",
  tableNeedsTwo: "Enter at least two “x, y” pairs with different x-values.",
  tableBadLines: "These lines are not a pair of numbers: {lines}.",
} as const;

export type InterpolationStrings =
  typeof LINEAR_INTERPOLATION_CALCULATOR_STRINGS;
type Mode = "two" | "table" | "bilinear";

export type InterpolationView =
  | { kind: "problem"; message: string }
  | {
      kind: "two";
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      x: number;
      y: number | null;
    }
  | { kind: "table"; points: XYPoint[]; x: number; y: number | null }
  | {
      kind: "bilinear";
      grid: {
        x1: number;
        x2: number;
        y1: number;
        y2: number;
        q11: number;
        q21: number;
        q12: number;
        q22: number;
      };
      x: number;
      y: number;
    };

type Fields = Record<string, string>;

/** Read each named field as a number; the first failure becomes the message. */
function readAll(
  fields: Fields,
  labels: Record<string, string>,
  strings: InterpolationStrings
): { values: Record<string, number> } | { error: string } {
  const values: Record<string, number> = {};
  for (const [key, text] of Object.entries(fields)) {
    const value = parseNumberInput(text);
    if (value === null) {
      return { error: t(strings.numberInvalid, { field: labels[key] ?? key }) };
    }
    values[key] = value;
  }
  return { values };
}

function twoView(f: Fields, strings: InterpolationStrings): InterpolationView {
  const read = readAll(
    { x1: f.x1, y1: f.y1, x2: f.x2, y2: f.y2, x: f.x },
    {
      x1: strings.x1Label,
      y1: strings.y1Label,
      x2: strings.x2Label,
      y2: strings.y2Label,
      x: strings.targetXLabel,
    },
    strings
  );
  if ("error" in read) {
    return { kind: "problem", message: read.error };
  }
  const v = read.values;
  if (v.x1 === v.x2) {
    return { kind: "problem", message: strings.sameX };
  }
  const target = f.y.trim() === "" ? null : parseNumberInput(f.y);
  if (f.y.trim() !== "" && target === null) {
    return {
      kind: "problem",
      message: t(strings.numberInvalid, { field: strings.targetYLabel }),
    };
  }
  return {
    kind: "two",
    x1: v.x1,
    y1: v.y1,
    x2: v.x2,
    y2: v.y2,
    x: v.x,
    y: target,
  };
}

function tableView(
  f: Fields,
  strings: InterpolationStrings
): InterpolationView {
  const { points, badLines } = parseXYPairs(f.table);
  if (badLines.length > 0) {
    return {
      kind: "problem",
      message: t(strings.tableBadLines, { lines: badLines.join(", ") }),
    };
  }
  const data = distinctSorted(points);
  if (data.length < 2) {
    return { kind: "problem", message: strings.tableNeedsTwo };
  }
  const x = parseNumberInput(f.x);
  if (x === null) {
    return {
      kind: "problem",
      message: t(strings.numberInvalid, { field: strings.targetXLabel }),
    };
  }
  const target = f.y.trim() === "" ? null : parseNumberInput(f.y);
  if (f.y.trim() !== "" && target === null) {
    return {
      kind: "problem",
      message: t(strings.numberInvalid, { field: strings.targetYLabel }),
    };
  }
  return { kind: "table", points: data, x, y: target };
}

function gridView(f: Fields, strings: InterpolationStrings): InterpolationView {
  const labels = {
    x1: strings.gridX1,
    x2: strings.gridX2,
    y1: strings.gridY1,
    y2: strings.gridY2,
    q11: strings.q11,
    q21: strings.q21,
    q12: strings.q12,
    q22: strings.q22,
    x: strings.gridTargetX,
    y: strings.gridTargetY,
  };
  const read = readAll(
    {
      x1: f.gx1,
      x2: f.gx2,
      y1: f.gy1,
      y2: f.gy2,
      q11: f.q11,
      q21: f.q21,
      q12: f.q12,
      q22: f.q22,
      x: f.gx,
      y: f.gy,
    },
    labels,
    strings
  );
  if ("error" in read) {
    return { kind: "problem", message: read.error };
  }
  const v = read.values;
  if (v.x1 === v.x2) {
    return { kind: "problem", message: strings.sameX };
  }
  if (v.y1 === v.y2) {
    return { kind: "problem", message: strings.sameY };
  }
  const { x, y, ...grid } = v;
  return {
    kind: "bilinear",
    grid: {
      x1: grid.x1,
      x2: grid.x2,
      y1: grid.y1,
      y2: grid.y2,
      q11: grid.q11,
      q21: grid.q21,
      q12: grid.q12,
      q22: grid.q22,
    },
    x,
    y,
  };
}

const INITIAL: Fields = {
  x1: "1",
  y1: "10",
  x2: "3",
  y2: "30",
  x: "2",
  y: "25",
  table: "0, 1\n20, 0.8\n40, 0.6\n60, 0.4",
  tableX: "30",
  tableY: "",
  gx1: "0",
  gx2: "10",
  gy1: "0",
  gy2: "20",
  q11: "1",
  q21: "3",
  q12: "5",
  q22: "11",
  gx: "5",
  gy: "10",
};

export default function LinearInterpolationCalculatorWidget() {
  const strings = useToolUi(LINEAR_INTERPOLATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("two");
  const [fields, setFields] = useState<Fields>(INITIAL);
  const set = (key: string) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const view = useMemo<InterpolationView>(() => {
    if (mode === "two") {
      return twoView(fields, strings);
    }
    if (mode === "table") {
      return tableView(
        { table: fields.table, x: fields.tableX, y: fields.tableY },
        strings
      );
    }
    return gridView(fields, strings);
  }, [mode, fields, strings]);
  const field = (key: string, label: string, hint?: string) => (
    <TextField
      hint={hint}
      id={`li-${key}`}
      label={label}
      mono
      numeric
      onChange={set(key)}
      value={fields[key] ?? ""}
    />
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="li-mode"
          label={strings.modeLabel}
          onChange={setMode}
          options={(["two", "table", "bilinear"] as const).map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        <div className="hidden sm:block" />
        {mode === "two" ? (
          <>
            {field("x1", strings.x1Label)}
            {field("y1", strings.y1Label)}
            {field("x2", strings.x2Label)}
            {field("y2", strings.y2Label)}
            {field("x", strings.targetXLabel)}
            {field("y", strings.targetYLabel)}
          </>
        ) : null}
        {mode === "table" ? (
          <>
            <div className="sm:col-span-2">
              <TextAreaField
                hint={strings.tableHint}
                id="li-table"
                label={strings.tableLabel}
                onChange={set("table")}
                placeholder={strings.tablePlaceholder}
                rows={6}
                value={fields.table ?? ""}
              />
            </div>
            {field("tableX", strings.targetXLabel)}
            {field("tableY", strings.targetYLabel)}
          </>
        ) : null}
        {mode === "bilinear" ? (
          <>
            {field("gx1", strings.gridX1)}
            {field("gx2", strings.gridX2)}
            {field("gy1", strings.gridY1)}
            {field("gy2", strings.gridY2)}
            {field("q11", strings.q11)}
            {field("q21", strings.q21)}
            {field("q12", strings.q12)}
            {field("q22", strings.q22)}
            {field("gx", strings.gridTargetX)}
            {field("gy", strings.gridTargetY)}
          </>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {view.kind === "problem" ? (
          <ErrorNotice>{view.message}</ErrorNotice>
        ) : null}
        {view.kind === "problem" ? null : (
          <InterpolationResults fmt={fmt} strings={strings} view={view} />
        )}
      </section>
    </>
  );
}
